// /appv3/carouselTypes.tsx — shared constants + card components for V3
// Carousel.
//
// Imported by both:
//   - /appv3/carousel/page.tsx (the standalone v1 fallback — still alive
//     during the test window per user direction Jun 14)
//   - /appv3/page.tsx (the unified Product-Shot-shaped V3 Carousel that
//     adds the type-pre-step on top of the existing Studio UI)
//
// Both surfaces send the same `carouselType` form field to backend's
// wizardAiGenerate → CAROUSEL_TYPE_SCAFFOLDS in helpers/ad_brief.py.
//
// AI_CHOOSE sentinel (Jun 14): when the user picks the 6th card ("Let AI
// choose"), `carouselType` is NOT sent to the backend — `type_mode`
// silently falls through to framings/beats so AI gets full discretion.

import type React from 'react'

// ─────────────────────────────────────────────────────────────────────
// Type constants
// ─────────────────────────────────────────────────────────────────────

export type CarouselTypeId =
  | 'bullet-list'
  | 'text-heavy-split'
  | 'story-arc'
  | 'before-after'
  | 'step-by-step'
  | 'ai-choose'

/** Sentinel — when picked, the page sends NO carouselType to backend
 *  (vs. one of the 5 structural scaffolds). Lets AI freestyle. */
export const AI_CHOOSE_TYPE_ID: CarouselTypeId = 'ai-choose'

export interface CarouselType {
  id: CarouselTypeId
  label: string
  description: string
  /** Default frame count when the user lands on this type. */
  defaultCount: 2 | 3 | 4
  /** Allowed frame counts. Story arc locks to 4; before/after allows 2 or 4. */
  allowedCounts: ReadonlyArray<2 | 3 | 4>
}

export const CAROUSEL_TYPES: readonly CarouselType[] = [
  {
    id: 'bullet-list',
    label: 'Bullet list',
    // Redesigned Jun 14 — was "one bullet per frame" (one big bullet
    // each frame). Now: each frame is a TOPIC with 3-5 short benefit
    // bullets stacked vertically (more useful for product carousels
    // showcasing multiple features at once).
    description: 'Each frame: a topic with 3-5 product benefit bullets.',
    defaultCount: 4,
    allowedCounts: [3, 4],
  },
  {
    id: 'text-heavy-split',
    label: 'Text + image',
    description: 'Half image, half text on each frame.',
    defaultCount: 4,
    allowedCounts: [3, 4],
  },
  {
    id: 'story-arc',
    label: 'Story arc',
    description: 'Hook → Build → Reveal → CTA across 4 frames.',
    defaultCount: 4,
    allowedCounts: [4],
  },
  {
    id: 'before-after',
    label: 'Before & after',
    description: 'Problem frames followed by solution frames.',
    defaultCount: 4,
    allowedCounts: [2, 4],
  },
  {
    id: 'step-by-step',
    label: 'Step by step',
    description: '"Step N of M" — instructional walkthrough.',
    defaultCount: 4,
    allowedCounts: [3, 4],
  },
  {
    id: 'ai-choose',
    label: 'Let AI choose',
    description: "Don't commit — AI picks the structure for your product.",
    defaultCount: 4,
    allowedCounts: [4],
  },
] as const

export function getCarouselType(id: CarouselTypeId): CarouselType {
  return CAROUSEL_TYPES.find((t) => t.id === id) ?? CAROUSEL_TYPES[0]
}

// ─────────────────────────────────────────────────────────────────────
// CarouselTypeCard — VERTICAL TILE adapted from My Creatives' SectionHero
// pattern (gallery/page.tsx). Same gradient backdrop + ambient blurred
// preview + fan-deck of 4 thumbnails, but stacked top-to-bottom (title
// above, deck below) so it reads as a tile instead of a wide row.
//
// Hover lifts the WHOLE card (scale + translateY + shadow). The old
// behavior on My Creatives only animated the inner fan thumbs — fixed
// here per user direction Jun 14.
//
// Sample images: /carousel-type-previews/<type-id>/frame{1..4}.png
// for the 5 structural types. AI Choose has no canonical frames, so it
// renders a sparkle visual instead of the deck.
//
// Two size variants:
//   - 'large' (V3 unified — Step 1 pre-step, 3×2 grid in main app)
//   - 'compact' (standalone v1 fallback — narrower)
// ─────────────────────────────────────────────────────────────────────

/** Fan offsets — directly mirrors `SectionHero` in `gallery/page.tsx`.
 *  Each thumb sits at translate(X, Y) rotate(R), z-ordered so the
 *  rightmost overlays the leftmost (deck-of-cards look). X offsets
 *  trimmed Jun 14 to leave room for w-36 (144px) thumbs PLUS the
 *  hover fan-spread (each inner div translates an extra 0.5× outward
 *  on group-hover, mirroring the My Creatives interaction). */
const FAN_OFFSETS: ReadonlyArray<{ rotate: number; x: number; y: number; z: number }> = [
  { rotate: -8, x: -26, y: 6, z: 1 },
  { rotate: -3, x:  -9, y: 1, z: 2 },
  { rotate:  3, x:   9, y: 1, z: 3 },
  { rotate:  8, x:  26, y: 6, z: 4 },
] as const

/** Hover-spread multipliers — each inner thumb translates an EXTRA
 *  (base × HOVER_X_FACTOR) on group-hover, on top of the outer div's
 *  base position. 0.5 matches gallery/SectionHero. The rotation factor
 *  is smaller (0.3) so cards spread more than they tilt — cleaner
 *  visually than equal scaling on both. */
const HOVER_X_FACTOR = 0.5
const HOVER_R_FACTOR = 0.3

export interface CarouselTypeCardProps {
  type: CarouselType
  selected: boolean
  onClick: () => void
  size?: 'large' | 'compact'
}

export function CarouselTypeCard({
  type, selected, onClick, size = 'large',
}: CarouselTypeCardProps): React.ReactElement {
  const isLarge = size === 'large'
  const isAiChoose = type.id === 'ai-choose'

  // Size tokens — large mirrors My Creatives' SectionHero typography
  // (text-xl lg:text-2xl headline, 9px eyebrow, xs body); compact
  // halves down for the standalone v1 fallback.
  const eyebrowSize = isLarge ? 'text-[9px]' : 'text-[8px]'
  const titleSize = isLarge ? 'text-xl lg:text-2xl' : 'text-base'
  const descSize = isLarge ? 'text-xs' : 'text-2xs'
  // Title gets px-5 for readability; fan gets the full card width so
  // the bigger thumbs can spread further on hover without clipping at
  // the rounded card edge.
  const titlePadding = isLarge ? 'px-5 pt-5' : 'px-3.5 pt-3.5'
  const fanPadding = isLarge ? 'pb-5' : 'pb-3.5'
  // Fan deck container + thumb sizing per variant. Bumped Jun 14 to
  // w-36 (144px, large) / w-28 (112px, compact) — the fan is the main
  // visual anchor of the tile. FAN_OFFSETS are sized to fit these thumbs
  // PLUS the hover spread inside the 250px card without clipping.
  const fanWrap = isLarge ? 'w-[240px] h-[170px]' : 'w-[180px] h-[130px]'
  const thumbSize = isLarge
    ? 'w-36 h-36 -mt-[72px] -ml-[72px]'
    : 'w-28 h-28 -mt-14 -ml-14'
  // Per-variant fan offset scaling — compact uses ~70% of the large
  // X/Y values so the deck reads at a smaller footprint without
  // recomputing the geometry by hand.
  const offsetScale = isLarge ? 1 : 0.7

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group relative w-full aspect-[3/4]
        rounded-2xl overflow-hidden text-left cursor-pointer
        bg-gradient-to-br from-surface via-white to-surface-dark/30
        border-2 transition-all duration-300 ease-out
        ${selected
          ? 'border-primary scale-[1.02] shadow-xl shadow-primary/25'
          : 'border-surface-dark/30 hover:border-primary/40 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl hover:shadow-dark/10'}
      `}
    >
      {/* Ambient backdrop — blurred first frame as mood layer. Skipped
          on AI Choose since it has no real images (sparkle visual
          handles the mood instead). */}
      {!isAiChoose && (
        <div
          className="absolute inset-0 opacity-25 blur-3xl scale-110"
          style={{
            backgroundImage: `url('/carousel-type-previews/${type.id}/frame1.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />
      )}

      {/* Vertical content stack: title block on top (padded for
          readability), fan deck below (full-width so big thumbs can
          spread without clipping at the rounded card edge). */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Title block — keeps horizontal padding for readability. */}
        <div className={`flex-shrink-0 ${titlePadding}`}>
          <p className={`${eyebrowSize} font-bold uppercase tracking-[0.18em] text-dark-muted mb-1`}>
            Carousel type
          </p>
          <p className={`font-display font-bold leading-none ${titleSize} ${selected ? 'text-primary' : 'text-dark'}`}>
            {type.label}
          </p>
          <p className={`${descSize} text-dark-muted mt-1.5 leading-snug line-clamp-2`}>
            {type.description}
          </p>
        </div>

        {/* Visual area: fan deck OR sparkle (AI Choose). flex-1 +
            items-center vertically centers the deck in remaining space.
            No horizontal padding so the fan can use the full card width. */}
        <div className={`relative flex-1 flex items-center justify-center mt-3 ${fanPadding}`}>
          {isAiChoose ? (
            <div className="flex flex-col items-center gap-2">
              <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-100 via-indigo-100 to-sky-100 backdrop-blur-sm flex items-center justify-center shadow-md">
                <i className="ri-sparkling-2-fill text-2xl text-primary" />
              </div>
              <div className="text-3xs font-bold tracking-[0.18em] text-dark/70">
                AI · MAGIC
              </div>
            </div>
          ) : (
            <div className={`relative ${fanWrap} pointer-events-none`}>
              {FAN_OFFSETS.map((fan, i) => {
                const n = i + 1
                // Base position (outer div) + hover fan-spread (inner
                // div) — split per gallery/SectionHero so the inner can
                // animate its translate without recomputing the outer's
                // inline style. The inner's translate lives in the
                // outer's rotated coordinate space, so each thumb fans
                // outward along its own tilt direction.
                return (
                  <div
                    key={n}
                    className={`absolute top-1/2 left-1/2 ${thumbSize}`}
                    style={{
                      transform: `translate(${fan.x * offsetScale}px, ${fan.y * offsetScale}px) rotate(${fan.rotate}deg)`,
                      zIndex: fan.z,
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-xl overflow-hidden border-[3px] border-white shadow-lg bg-surface/60 transition-transform duration-300 ease-out group-hover:translate-x-[var(--fan-x-hover)] group-hover:translate-y-[var(--fan-y-hover)] group-hover:rotate-[var(--fan-r-hover)]"
                      style={{
                        backgroundImage: `url('/carousel-type-previews/${type.id}/frame${n}.png')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        // @ts-expect-error custom CSS vars
                        '--fan-x-hover': `${fan.x * offsetScale * HOVER_X_FACTOR}px`,
                        '--fan-y-hover': `${fan.y * offsetScale * HOVER_X_FACTOR}px`,
                        '--fan-r-hover': `${fan.rotate * HOVER_R_FACTOR}deg`,
                      }}
                      aria-hidden="true"
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selected check badge */}
      {selected && (
        <div className={`absolute ${isLarge ? 'top-3 right-3 w-7 h-7' : 'top-2 right-2 w-5 h-5'} rounded-full bg-primary text-white flex items-center justify-center shadow-md z-20`}>
          <i className={`ri-check-line ${isLarge ? 'text-sm' : 'text-xs'}`} />
        </div>
      )}
    </button>
  )
}
